import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Card, Table, Button, Form, Row, Col, Badge, Spinner, Alert, Modal
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../layouts/Header';
import axiosInstance from '../services/axiosInterceptor';

const STATUS = {
  unpaid: { label: 'ค้างชำระ', bg: 'danger' },
  partial: { label: 'ชำระบางส่วน', bg: 'warning' },
  paid: { label: 'ชำระครบ', bg: 'success' },
  cancelled: { label: 'ยกเลิก', bg: 'secondary' }
};
const STREAM = { general: 'สามัญ', religious: 'ศาสนา', common: 'ทุกคน' };
const METHOD = { cash: 'เงินสด', transfer: 'โอน' };
const baht = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });

const FeeInvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPay, setShowPay] = useState(false);
  const [saving, setSaving] = useState(false);
  const [payForm, setPayForm] = useState({ pay_amount: '', pay_date: '', pay_method: 'cash', receipt_no: '', payment_note: '' });

  const fetchInvoice = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosInstance.get(`/api/fee/invoices/${invoiceId}`);
      if (res.data.success) setInvoice(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดใบแจ้งหนี้ไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  const openPay = () => {
    setPayForm({
      pay_amount: invoice ? invoice.balance : '',
      pay_date: new Date().toISOString().substring(0, 10),
      pay_method: 'cash',
      receipt_no: '',
      payment_note: ''
    });
    setShowPay(true);
  };

  const handlePay = async () => {
    const amount = Number(payForm.pay_amount);
    if (!amount || amount <= 0) { setError('กรุณากรอกจำนวนเงินที่ถูกต้อง'); return; }
    setSaving(true);
    setError('');
    try {
      await axiosInstance.post(`/api/fee/invoices/${invoiceId}/payments`, payForm);
      setShowPay(false);
      fetchInvoice();
    } catch (err) {
      setError(err.response?.data?.message || 'บันทึกการชำระไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('ยกเลิกใบแจ้งหนี้นี้?')) return;
    try {
      await axiosInstance.put(`/api/fee/invoices/${invoiceId}/cancel`);
      fetchInvoice();
    } catch (err) {
      setError(err.response?.data?.message || 'ยกเลิกไม่สำเร็จ');
    }
  };

  const handlePrint = () => {
    if (!invoice) return;
    const itemsHtml = invoice.items.map((it) => `
      <tr>
        <td style="border:1px solid #ccc;padding:6px;">${it.item_name_snapshot}</td>
        <td style="border:1px solid #ccc;padding:6px;text-align:center;">${STREAM[it.stream] || it.stream}</td>
        <td style="border:1px solid #ccc;padding:6px;text-align:right;">${baht(it.amount)}</td>
      </tr>`).join('');
    const payHtml = invoice.payments.map((p) => `
      <tr>
        <td style="border:1px solid #ccc;padding:6px;">${new Date(p.pay_date).toLocaleDateString('th-TH')}</td>
        <td style="border:1px solid #ccc;padding:6px;">${METHOD[p.pay_method] || p.pay_method}</td>
        <td style="border:1px solid #ccc;padding:6px;text-align:right;">${baht(p.pay_amount)}</td>
      </tr>`).join('');

    const w = window.open('', '_blank', 'width=800,height=900');
    w.document.write(`
      <html><head><title>${invoice.invoice_no}</title>
      <style>body{font-family:Tahoma,Arial,sans-serif;color:#111;padding:24px;}
      table{border-collapse:collapse;width:100%;margin-top:8px;}
      th{border:1px solid #ccc;padding:6px;background:#f1f3f5;text-align:left;}</style>
      </head><body>
      <h2 style="margin:0 0 4px;">ใบแจ้งหนี้ / ใบเสร็จรับเงิน</h2>
      <div>โรงเรียนวัฒนธรรมอิสลาม</div>
      <hr/>
      <div><strong>เลขที่บิล:</strong> ${invoice.invoice_no}</div>
      <div><strong>นักเรียน:</strong> ${invoice.st_name} (${invoice.st_idnumber})</div>
      <div><strong>ปีการศึกษา/ภาคเรียน:</strong> ${invoice.year_year} / ${invoice.term_semester}</div>
      <div><strong>สถานะ:</strong> ${STATUS[invoice.invoice_status]?.label || invoice.invoice_status}</div>
      <h3 style="margin:16px 0 4px;">รายการค่าใช้จ่าย</h3>
      <table><thead><tr><th>รายการ</th><th style="text-align:center;">สาย</th><th style="text-align:right;">จำนวนเงิน</th></tr></thead>
      <tbody>${itemsHtml}</tbody></table>
      <div style="text-align:right;margin-top:8px;"><strong>รวมทั้งสิ้น: ${baht(invoice.total_amount)} บาท</strong></div>
      <div style="text-align:right;">ชำระแล้ว: ${baht(invoice.paid_amount)} บาท</div>
      <div style="text-align:right;"><strong>คงค้าง: ${baht(invoice.balance)} บาท</strong></div>
      ${invoice.payments.length ? `<h3 style="margin:16px 0 4px;">ประวัติการชำระ</h3>
      <table><thead><tr><th>วันที่</th><th>ช่องทาง</th><th style="text-align:right;">จำนวนเงิน</th></tr></thead>
      <tbody>${payHtml}</tbody></table>` : ''}
      <p style="margin-top:32px;">ออกเมื่อ ${new Date().toLocaleString('th-TH')}</p>
      </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  if (loading) {
    return (
      <React.Fragment>
        <Header />
        <main className="main main-app p-3 p-lg-4">
          <Container fluid className="px-0 text-center py-5"><Spinner animation="border" /></Container>
        </main>
      </React.Fragment>
    );
  }

  if (!invoice) {
    return (
      <React.Fragment>
        <Header />
        <main className="main main-app p-3 p-lg-4">
          <Container fluid className="px-0">
            <Alert variant="danger">{error || 'ไม่พบใบแจ้งหนี้'}</Alert>
            <Button variant="secondary" onClick={() => navigate('/fee/invoices')}>กลับ</Button>
          </Container>
        </main>
      </React.Fragment>
    );
  }

  const isCancelled = invoice.invoice_status === 'cancelled';
  const isPaid = invoice.invoice_status === 'paid';

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <Container fluid className="px-0">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <Button variant="link" className="p-0 mb-1" onClick={() => navigate('/fee/invoices')}>
                <i className="ri-arrow-left-line me-1" />กลับรายการบิล
              </Button>
              <h2 className="mb-0">{invoice.invoice_no}</h2>
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" onClick={handlePrint}>
                <i className="ri-printer-line me-1" />พิมพ์
              </Button>
              {!isCancelled && !isPaid && (
                <Button variant="primary" onClick={openPay}>
                  <i className="ri-money-dollar-circle-line me-1" />รับชำระ
                </Button>
              )}
            </div>
          </div>

          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

          <Row className="g-3 mb-3">
            <Col lg={4}>
              <Card className="border-0 shadow-sm h-100"><Card.Body>
                <h6 className="text-muted mb-2">ข้อมูลนักเรียน</h6>
                <div className="fw-medium">{invoice.st_name}</div>
                <div className="text-muted">{invoice.st_idnumber}</div>
                <hr />
                <div className="d-flex justify-content-between"><span className="text-muted">ปี/ภาค</span><span>{invoice.year_year} / {invoice.term_semester}</span></div>
                <div className="d-flex justify-content-between"><span className="text-muted">สถานะ</span>
                  <Badge bg={STATUS[invoice.invoice_status]?.bg}>{STATUS[invoice.invoice_status]?.label}</Badge>
                </div>
              </Card.Body></Card>
            </Col>
            <Col lg={8}>
              <Card className="border-0 shadow-sm h-100"><Card.Body>
                <Row className="text-center">
                  <Col><small className="text-muted d-block">ยอดรวม</small><h4 className="mb-0">{baht(invoice.total_amount)}</h4></Col>
                  <Col><small className="text-muted d-block">ชำระแล้ว</small><h4 className="mb-0 text-success">{baht(invoice.paid_amount)}</h4></Col>
                  <Col><small className="text-muted d-block">คงค้าง</small><h4 className="mb-0 text-danger">{baht(invoice.balance)}</h4></Col>
                </Row>
                {!isCancelled && !isPaid && invoice.payments.length === 0 && (
                  <div className="text-end mt-3">
                    <Button size="sm" variant="outline-danger" onClick={handleCancel}>ยกเลิกบิล</Button>
                  </div>
                )}
              </Card.Body></Card>
            </Col>
          </Row>

          <Card className="border-0 shadow-sm mb-3">
            <Card.Header className="bg-white"><strong>รายการค่าใช้จ่าย</strong></Card.Header>
            <Card.Body className="p-0">
              <Table className="mb-0">
                <thead className="table-light">
                  <tr><th>รายการ</th><th className="text-center">สาย</th><th className="text-end">จำนวนเงิน</th></tr>
                </thead>
                <tbody>
                  {invoice.items.map((it) => (
                    <tr key={it.inv_item_id}>
                      <td>{it.item_name_snapshot}</td>
                      <td className="text-center"><Badge bg="light" text="dark">{STREAM[it.stream] || it.stream}</Badge></td>
                      <td className="text-end">{baht(it.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="table-light"><th colSpan={2}>รวม</th><th className="text-end">{baht(invoice.total_amount)}</th></tr>
                </tfoot>
              </Table>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white"><strong>ประวัติการชำระ</strong></Card.Header>
            <Card.Body className="p-0">
              {invoice.payments.length === 0 ? (
                <div className="text-center text-muted py-4">ยังไม่มีการชำระ</div>
              ) : (
                <Table className="mb-0">
                  <thead className="table-light">
                    <tr><th>เลขที่</th><th>วันที่</th><th>ช่องทาง</th><th>เลขใบเสร็จ</th><th>ผู้รับเงิน</th><th className="text-end">จำนวนเงิน</th></tr>
                  </thead>
                  <tbody>
                    {invoice.payments.map((p) => (
                      <tr key={p.payment_id}>
                        <td>{p.payment_no}</td>
                        <td>{new Date(p.pay_date).toLocaleDateString('th-TH')}</td>
                        <td>{METHOD[p.pay_method] || p.pay_method}</td>
                        <td>{p.receipt_no || '-'}</td>
                        <td>{p.received_by || '-'}</td>
                        <td className="text-end">{baht(p.pay_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </main>

      <Modal show={showPay} onHide={() => setShowPay(false)} centered>
        <Modal.Header closeButton><Modal.Title>รับชำระเงิน</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="mb-3 p-2 bg-light rounded">
            ยอดคงค้าง: <strong className="text-danger">{baht(invoice.balance)}</strong> บาท
          </div>
          <Row className="g-3">
            <Col xs={6}>
              <Form.Label>จำนวนเงิน (บาท)</Form.Label>
              <Form.Control type="number" min="0" max={invoice.balance}
                value={payForm.pay_amount}
                onChange={(e) => setPayForm({ ...payForm, pay_amount: e.target.value })} />
            </Col>
            <Col xs={6}>
              <Form.Label>วันที่ชำระ</Form.Label>
              <Form.Control type="date" value={payForm.pay_date}
                onChange={(e) => setPayForm({ ...payForm, pay_date: e.target.value })} />
            </Col>
            <Col xs={6}>
              <Form.Label>ช่องทาง</Form.Label>
              <Form.Select value={payForm.pay_method}
                onChange={(e) => setPayForm({ ...payForm, pay_method: e.target.value })}>
                <option value="cash">เงินสด</option>
                <option value="transfer">โอน</option>
              </Form.Select>
            </Col>
            <Col xs={6}>
              <Form.Label>เลขใบเสร็จ (ไม่บังคับ)</Form.Label>
              <Form.Control value={payForm.receipt_no}
                onChange={(e) => setPayForm({ ...payForm, receipt_no: e.target.value })} />
            </Col>
            <Col xs={12}>
              <Form.Label>หมายเหตุ</Form.Label>
              <Form.Control as="textarea" rows={2} value={payForm.payment_note}
                onChange={(e) => setPayForm({ ...payForm, payment_note: e.target.value })} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPay(false)}>ยกเลิก</Button>
          <Button variant="primary" onClick={handlePay} disabled={saving}>
            {saving ? 'กำลังบันทึก...' : 'บันทึกการชำระ'}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default FeeInvoiceDetail;
